// WorkspaceDeck: the workspace home's plane, and the only thing on it: the
// workspaces being worked in, side by side, each the whole board (its clock
// and window, lanes, findings, readings and notes; lib/traceCanvasStore.js).
// What there is to bring onto them is not on the plane: it is in the sheet
// over the page's left side (the add panel), where the sessions' list is,
// listed at one size to read: what met a standard, where Triage left every
// finding, the machines, and every session. Anything
// in the sheet is dragged onto any workspace, or clicked onto the one being
// worked in (the pane last pointed into). A session is laid on; a finding is
// kept, open on its session's card; a machine is kept as a card of its
// sessions; a search (a file path, a topic, any query the session search
// reads) is kept as a card of the sessions it matches; a note is the
// reader's own words, loose along the board's top. A pane without a
// workspace is where one starts: from whatever is dropped there or picked
// while it is the one being worked in, from a note, or from a search.
import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import HoverTooltip from "./HoverTooltip.jsx";
import OverflowTooltip from "./OverflowTooltip.jsx";
import TraceCanvas from "./TraceCanvas.jsx";
import WorkspaceAddPanel from "./WorkspaceAddOverlay.jsx";
import { fleetSources } from "./FleetSources.jsx";
import FleetMap from "./FleetMap.jsx";
import { STEPS } from "./FlowBoard.jsx";
import { openGraphCanvas, workspaceTraces } from "./InvestigationGraphPill.jsx";
import { machineLabel, outcomesOf, useFleetModel } from "./fleetModel.js";
import { query } from "../lib/dataClient.js";
import { onDeckWorkspace, setDeckBlankInFront } from "../lib/deckRequests.js";
import { getDockChatBar, subscribeDock } from "../lib/dockStore.js";
import { activeGraphKey, graphVersion, readLiveGraph, subscribeGraph } from "../lib/investigationGraphRun.js";
import { parseGraphFocus } from "../lib/traceGraphOverlay.js";
import { SESSION_MATCH_CAP, searchStrings } from "../lib/sessionMatch.js";
import { WORKSPACE_DRAG_TYPE, draggedItem, draggingItem, droppedItem, setDragItem } from "../lib/dragTypes.js";
import { noteBoardDragOver, noteBoardDrop } from "../lib/boardDrops.js";
import { clearBoardCard, clearBoardPick, setBoardCard, setBoardPick } from "../lib/boardPickStore.js";
import { requestWorkspaceFocus } from "../lib/workspaceStore.js";
import { PaneFootContext } from "../lib/paneFoot.js";
import { addPicks, appendClause, setWorkspaceSearch, togglePick, useWorkspacePicks } from "../lib/workspaceSearch.js";
import { flaggedOnly, splitReview } from "../lib/reviewClause.js";
import { readFleetPlaces } from "../lib/fleetFindings.js";
import { askFlaggedReach } from "../lib/flaggedReach.js";
import {
  chatCanvasId, emptyTraceCanvas, forgetEmptyTraceCanvas, traceCanvasDeleted, keepOnTraceCanvas, keepTraceCanvas, layOnTraceCanvas, newTraceCanvas, reaches, releaseTraceCanvasInDeck, renameTraceCanvas, setTraceCanvasTitle, showTraceCanvasInDeck, traceCanvasHolds, traceCanvasKept,
  traceCanvasSnapshot, useTraceCanvas, useTraceCanvases, useTracePreview,
} from "../lib/traceCanvasStore.js";

const KEY = "origin-workspace-deck-v1";
const MAX_PANES = 4;
const PANE_MIN = 360;
const GUTTER = 10;
// A pane's foot: the board's bar, or a start pane's own.
const BAR_H = 32;
const newId = () => `p${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
// A pane: the workspace it shows, the workspaces it went in through to get
// there (`trail`, outermost first), and its share of the room.
const freshPane = (ws = null, w = 1) => ({ id: newId(), ws, trail: [], w });
const MAX_TRAIL = 8;
// How long a board takes to open out of the card it was.
const OPEN_MS = 300;

const cell = "flex items-center gap-[6px] whitespace-nowrap border-l border-[color:var(--line-soft)] px-[9px] leading-[16px] text-[color:var(--muted)] hover:text-[color:var(--ink)]";
const cellOn = "[background-image:linear-gradient(var(--green-soft),var(--green-soft))] text-[color:var(--ink)]";
const iconCell = "flex w-[26px] flex-none items-center justify-center border-l border-[color:var(--line-soft)] text-[12px] text-[color:var(--muted)] hover:text-[color:var(--ink)]";

// -- what is kept -------------------------------------------------------------------

// The panes, each the workspace it shows (or none) and its share of the room;
// the one worked in; and whether the sheet is up, on which of its lists.
function cleanDeck(s) {
  const seen = new Set();
  const panes = (Array.isArray(s?.panes) ? s.panes : []).filter((p) => p?.id).slice(0, MAX_PANES).map((p) => {
    const ws = typeof p.ws === "string" && p.ws && !seen.has(p.ws) ? p.ws : null;
    if (ws) seen.add(ws);
    const trail = ws && Array.isArray(p.trail) ? p.trail.filter((x) => typeof x === "string" && x && x !== ws).slice(-MAX_TRAIL) : [];
    return { id: String(p.id), ws, trail, w: p.w > 0 ? Number(p.w) : 1 };
  });
  const list = panes.length ? panes : [freshPane()];
  return {
    v: 1,
    panes: list,
    active: list.some((p) => p.id === s?.active) ? s.active : list[0].id,
    // Shut until asked for: the sidebar is where things are found.
    sheet: s?.sheet === true,
    tab: typeof s?.tab === "string" ? s.tab : "look",
  };
}
// The panes come back as they were left in this tab (a reload, another mode
// and back); a new tab or a new browser session opens on the range's board,
// not on the workspace last worked in, which stays kept to open from the
// sidebar. Only the sheet's list is kept across sessions.
function readDeck() {
  try {
    const s = JSON.parse(globalThis.sessionStorage?.getItem(KEY) || "null");
    if (s?.v === 1) return cleanDeck(s);
    const kept = JSON.parse(globalThis.localStorage?.getItem(KEY) || "null");
    return cleanDeck({ tab: kept?.tab });
  } catch { /* a fresh deck */ }
  return cleanDeck(null);
}
const writeDeck = (value) => {
  try { globalThis.sessionStorage?.setItem(KEY, JSON.stringify(value)); } catch { /* this load only */ }
  try { globalThis.localStorage?.setItem(KEY, JSON.stringify({ v: 1, tab: value.tab })); } catch { /* this tab only */ }
};

// -- what a workspace's searches match -----------------------------------------------

// The sessions a search matches in the range, newest activity first, with
// what each was about: read once per search and range, kept a minute, shared
// by every pane.
const SEARCH_KEEP_MS = 60_000;
const SEARCH_TITLED = 40;
const searchReads = new Map();
const rangeKeyOf = (range) => (typeof range === "string" ? range : JSON.stringify(range ?? null));
export function readSearch(q, range) {
  const key = `${q}\n${rangeKeyOf(range)}`;
  const hit = searchReads.get(key);
  if (hit && Date.now() - hit.at < SEARCH_KEEP_MS) return hit.promise;
  const promise = (async () => {
    const { rest, keeps } = splitReview(q);
    const places = keeps ? await readFleetPlaces(range) : null;
    // Where review left a session, applied as the board applies it.
    const within = flaggedOnly(keeps) ? [...places].filter(([, place]) => keeps(place)).map(([id]) => id) : null;
    // The flagged sessions it keeps, read as the page reads them (shared).
    const reached = within?.length ? (await askFlaggedReach({ range, rest, ids: within }).promise).reached : null;
    const out = within ? { sessions: reached ? within.map((id) => reached.get(String(id).toLowerCase())).filter(Boolean).sort((a, b) => (Number(b.lastMs) || 0) - (Number(a.lastMs) || 0)).slice(0, SESSION_MATCH_CAP) : [] }
      : await query("getSessionMatches", { scopePath: [], range, query: rest });
    const list = (out?.sessions || []).filter((x) => !keeps || keeps(places.get(String(x.id).toLowerCase()) || "none"));
    const ids = list.slice(0, SEARCH_TITLED).map((x) => x.id);
    const heads = ids.length ? await query("getSessionHeads", { ids, range }).catch(() => ({})) : {};
    return list.map((x) => ({ id: String(x.id).toLowerCase(), title: heads?.[x.id]?.summary || "", firstMs: x.firstMs, lastMs: x.lastMs }));
  })();
  searchReads.set(key, { at: Date.now(), promise });
  promise.catch(() => searchReads.delete(key));
  return promise;
}
// A workspace's searches, each with the sessions it matches (and, from the
// fleet, what each was about and what was found in it) and the strings it
// looks for in them, for its board.
function useSearchRows(searches, range, model) {
  const [got, setGot] = useState(() => new Map());
  const rangeKey = rangeKeyOf(range);
  const qs = useMemo(() => [...new Set((searches || []).map((x) => x.q))], [searches]);
  const qsKey = qs.join("\n");
  useEffect(() => {
    let live = true;
    for (const q of qs) {
      const done = (rows) => { if (live) setGot((cur) => new Map(cur).set(`${q}\n${rangeKey}`, rows)); };
      readSearch(q, range).then(done, () => done([]));
    }
    return () => { live = false; };
  }, [qsKey, rangeKey]); // eslint-disable-line react-hooks/exhaustive-deps
  return useMemo(() => (searches?.length ? searches.map((x) => {
    const rows = got.get(`${x.q}\n${rangeKey}`);
    return {
      id: x.id, name: x.label || x.q, status: rows ? "done" : "reading", strings: searchStrings(x.q),
      sessions: (rows || []).map((r) => {
        const fs = model.sessions.get(r.id);
        return { ...r, title: r.title || fs?.title || "", findings: fs?.findings || [] };
      }),
    };
  }) : null), [searches, got, rangeKey, model]);
}

// When sessions the fleet does not hold (or holds without what it was about)
// ran, and what each was about: read once per session and range, kept a
// minute.
const spanReads = new Map();
function useSpans(ids, range) {
  const [got, setGot] = useState(() => new Map());
  const rangeKey = rangeKeyOf(range);
  const key = ids.join(",");
  useEffect(() => {
    if (!ids.length) return undefined;
    let live = true;
    const want = ids.filter((id) => {
      const hit = spanReads.get(`${id}\n${rangeKey}`);
      return !hit || Date.now() - hit.at > SEARCH_KEEP_MS;
    });
    const fill = () => setGot(() => new Map(ids.map((id) => [id, spanReads.get(`${id}\n${rangeKey}`)?.head]).filter(([, h]) => h)));
    if (!want.length) { fill(); return undefined; }
    query("getSessionHeads", { ids: want, range, whole: true }).then((out) => {
      for (const id of want) spanReads.set(`${id}\n${rangeKey}`, { at: Date.now(), head: out?.[id] || null });
      if (live) fill();
    }, () => {});
    return () => { live = false; };
  }, [key, rangeKey]); // eslint-disable-line react-hooks/exhaustive-deps
  return got;
}
// The workspaces inside one, each as its card shows it: its name, what it
// holds, and its sessions where they ran.
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
function useNestedRows(nested, range, model, workspaces) {
  const recs = useMemo(() => (nested || []).filter((w) => traceCanvasKept(w.id)).map((w) => ({
    id: w.id, rec: traceCanvasSnapshot(w.id), listed: workspaces.find((x) => x.id === w.id),
  })), [nested, workspaces]);
  const ids = useMemo(() => [...new Set(recs.flatMap(({ rec }) => [rec.anchor, ...rec.layered.map((t) => t.id)].filter(Boolean)))].filter((id) => !model.sessions.get(id)?.title), [recs, model]);
  const spans = useSpans(ids, range);
  return useMemo(() => (recs.length ? recs.map(({ id, rec, listed }) => {
    // A session by what it did (its own title, before whatever it was laid
    // on as), and where it ran.
    const sessions = [rec.anchor ? { id: rec.anchor, title: rec.title } : null, ...rec.layered].filter(Boolean).map((t) => {
      const fs = model.sessions.get(t.id);
      const sp = spans.get(t.id);
      return {
        id: t.id, title: fs?.title || sp?.summary || t.title || "", endpointId: fs?.endpointId || sp?.endpointId || null,
        firstMs: fs?.firstMs ?? sp?.firstMs ?? null, lastMs: fs?.lastMs ?? sp?.lastMs ?? null, findings: fs?.findings || [],
      };
    });
    const sub = [
      sessions.length ? plural(sessions.length, "session") : null,
      rec.looseNotes.length ? plural(rec.looseNotes.length, "note") : null,
      rec.searches.length ? plural(rec.searches.length, "search", "searches") : null,
      rec.machines.length ? plural(rec.machines.length, "machine") : null,
      rec.nested.length ? plural(rec.nested.length, "workspace") : null,
    ].filter(Boolean).join(" \u00b7 ");
    return { id, name: listed?.title || rec.title || "Untitled workspace", sub: sub || "nothing on it yet", sessions, note: rec.looseNotes[0]?.text || "" };
  }) : null), [recs, model, spans]);
}

// -- the panes ----------------------------------------------------------------------

// The seam between two panes: the knob shows on hover, and a drag shares the
// two panes' room between them.
export function PaneDivider({ onStart, onMove, onEnd }) {
  const [active, setActive] = useState(false);
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize the panes"
      className="group relative flex w-[10px] flex-none cursor-col-resize items-center justify-center"
      onPointerDown={(e) => {
        if (e.button !== 0) return;
        try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* capture is an enhancement */ }
        setActive(true);
        onStart(e.clientX);
      }}
      // A move with no button held ends a drag whose release was missed.
      onPointerMove={(e) => { if (!active) return; if (!(e.buttons & 1)) { setActive(false); onEnd(); return; } onMove(e.clientX); }}
      onPointerUp={() => { if (active) { setActive(false); onEnd(); } }}
      onPointerCancel={() => { if (active) { setActive(false); onEnd(); } }}
      onLostPointerCapture={() => { if (active) { setActive(false); onEnd(); } }}
    >
      <span className={`h-[48px] w-[3px] rounded-full bg-[color:var(--line-strong)] transition-opacity ${active ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`} />
    </div>
  );
}

// Which workspace a pane shows: any kept in this browser, or none; the one
// shown named again in place; any carried from the list onto a board, to go
// inside it. `dim`: the pane is not the one being worked in; `edge`, the side
// its rule is on.
function WorkspaceMenu({ workspaces, current, onPick, dim = false, edge = null }) {
  const [open, setOpen] = useState(false);
  const [naming, setNaming] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    window.addEventListener("pointerdown", away, true);
    return () => window.removeEventListener("pointerdown", away, true);
  }, [open]);
  const cur = current ? workspaces.find((w) => w.id === current) : null;
  const item = "flex w-full items-center justify-between gap-[14px] px-[10px] py-[5px] text-left text-[11.5px] leading-[16px]";
  if (naming && cur) {
    const done = (e, keep) => {
      if (keep) renameTraceCanvas(cur.id, e.currentTarget.value);
      setNaming(false);
    };
    return (
      <div className={`flex min-w-0 flex-[0_1_auto] items-stretch ${edge === "r" ? "border-r border-[color:var(--line-soft)]" : ""}`} data-deck-workspaces="">
        <input
          // A fresh field, focused as it comes up.
          ref={(el) => { if (el && document.activeElement !== el) { el.focus(); el.select(); } }}
          defaultValue={cur.title || ""}
          placeholder="Name this workspace"
          aria-label="Name this workspace"
          onKeyDown={(e) => {
            if (e.key === "Enter") done(e, true);
            else if (e.key === "Escape") done(e, false);
          }}
          onBlur={(e) => done(e, true)}
          className="w-[200px] min-w-0 bg-[color:var(--control-bg)] px-[9px] text-[11px] leading-[16px] text-[color:var(--ink)] outline-none placeholder:text-[color:var(--muted-soft)]"
          data-deck-name=""
        />
      </div>
    );
  }
  return (
    <div ref={ref} className={`relative flex min-w-0 flex-[0_1_auto] items-stretch ${edge === "r" ? "border-r border-[color:var(--line-soft)]" : ""}`} data-deck-workspaces="">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        onDoubleClick={() => { if (cur) { setOpen(false); setNaming(true); } }}
        aria-haspopup="menu"
        aria-expanded={open}
        className={`flex min-w-[96px] max-w-[240px] items-center gap-[6px] whitespace-nowrap px-[9px] leading-[16px] ${dim ? "text-[color:var(--muted)] hover:text-[color:var(--ink)]" : "text-[color:var(--ink)]"}`}
      >
        <span className="min-w-0 truncate">{cur ? cur.title || "Untitled workspace" : "No workspace"}</span>
        {cur?.count ? <span className="tabular-nums text-[color:var(--muted-soft)]">{cur.count}</span> : null}
        <svg viewBox="0 0 10 10" className="h-[8px] w-[8px] flex-none text-[color:var(--muted)]" aria-hidden="true"><path d="M2 6.5 5 3.5 8 6.5" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </button>
      {open ? (
        <div role="menu" className="absolute bottom-[calc(100%+6px)] left-0 z-[20] grid max-h-[340px] w-[300px] overflow-y-auto rounded-[2px] border border-[color:var(--line-strong)] bg-[color:var(--panel)] py-[4px] shadow-[var(--node-shadow-lift)]">
          {workspaces.length ? workspaces.map((w) => (
            <button
              key={w.id}
              type="button"
              role="menuitem"
              draggable
              onDragStart={(e) => setDragItem(e, { kind: "workspace", id: w.id, title: w.title })}
              onDragEnd={() => setOpen(false)}
              onClick={() => { onPick(w.id); setOpen(false); }}
              className={`${item} ${w.id === current ? `text-[color:var(--ink)] ${cellOn}` : "text-[color:var(--muted)] hover:bg-[color:var(--gray-fill)] hover:text-[color:var(--ink)]"}`}
            >
              <OverflowTooltip text={w.title || "Untitled workspace"} className="min-w-0" />
              {w.count ? <span className="flex-none tabular-nums text-[color:var(--muted-soft)]">{w.count}</span> : null}
            </button>
          )) : (
            <div className="px-[10px] py-[6px] text-[11.5px] leading-[1.45] text-[color:var(--muted)]">No workspaces kept in this browser yet. Bring a session onto this pane to start one.</div>
          )}
          {current ? (
            <button type="button" role="menuitem" onClick={() => { setOpen(false); setNaming(true); }} className={`${item} border-t border-[color:var(--line-soft)] text-[color:var(--muted)] hover:bg-[color:var(--gray-fill)] hover:text-[color:var(--ink)]`} data-deck-rename="">
              Rename this workspace
            </button>
          ) : null}
          {current ? (
            <button type="button" role="menuitem" onClick={() => { onPick(null); setOpen(false); }} className={`${item} text-[color:var(--muted)] hover:bg-[color:var(--gray-fill)] hover:text-[color:var(--ink)]`}>
              Start another workspace here
            </button>
          ) : null}
          {workspaces.length > 1 ? (
            <div className="border-t border-[color:var(--line-soft)] px-[10px] pb-[2px] pt-[5px] text-[11px] leading-[1.45] text-[color:var(--muted-soft)]">Drag one onto a board to put it inside that workspace.</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// Where a drop lands: a cue over the pane saying what the drop does.
// Over a board, the board itself shows where the thing would land; the cue
// only says what letting go does, at the board's top, clear of it.
function DropCue({ text, slim = false }) {
  if (slim) {
    return (
      <div className="pointer-events-none absolute inset-x-0 top-[10px] z-[30] flex justify-center" data-deck-drop="">
        <span className="rounded-[2px] border border-[color:var(--line-strong)] bg-[color:var(--panel)] px-[10px] py-[5px] text-[12px] text-[color:var(--ink)] shadow-[var(--node-shadow-lift)]">{text}</span>
      </div>
    );
  }
  return (
    <div className="pointer-events-none absolute inset-0 z-[30] grid place-items-center rounded-[2px] border border-dashed border-[color:var(--line-strong)] bg-[color:color-mix(in_srgb,var(--green-soft)_70%,transparent)]" data-deck-drop="">
      <span className="rounded-[2px] border border-[color:var(--line-strong)] bg-[color:var(--panel)] px-[10px] py-[5px] text-[12px] text-[color:var(--ink)] shadow-[var(--node-shadow-lift)]">{text}</span>
    </div>
  );
}

// What a search box's start puts on a workspace: its search (when it has
// clauses) and the sessions picked into it.
const startThings = (q, sessions = []) => ({ ...(q ? { searches: [{ q, label: q }] } : {}), ...(sessions.length ? { sessions } : {}) });

// A pane with no workspace: the range's AI work as one map (FleetMap), read
// through the workspace's one search; anything dropped on it starts a
// workspace, as the search and the sessions picked into it do.
function Start({ lost, cover, onStart, range, active = true }) {
  return (
    <div className="h-full min-h-0" style={{ paddingLeft: cover }} data-deck-start="">
      <FleetMap range={range} lost={lost} inFront={active} onStartSearch={(q, sessions) => onStart(startThings(q, sessions))} />
    </div>
  );
}

// A pane's own controls: Split, and its close while it is not the only one.
function PaneEnds({ paneId, count, canSplit, api }) {
  return (
    <>
      {canSplit ? (
        <HoverTooltip placement="top" label="Another pane beside this one, for another workspace">
          <button type="button" className={cell} onClick={() => api.split(paneId)} data-deck-split="">Split</button>
        </HoverTooltip>
      ) : null}
      {count > 1 ? (
        <HoverTooltip placement="top" label="Close this pane; its workspace stays kept">
          <button type="button" className={iconCell} onClick={() => api.close(paneId)} aria-label="Close this pane" data-deck-pane-close="">&times;</button>
        </HoverTooltip>
      ) : null}
    </>
  );
}

// Where a pane went in from: the workspaces outside the one it shows, each a
// step back out.
function Trail({ trail, onOut }) {
  if (!trail.length) return null;
  return (
    <div className="flex min-w-0 flex-[0_1_auto] items-stretch" data-deck-trail="">
      {trail.map((id, i) => (
        <HoverTooltip key={id} placement="top" label="Back out to this workspace">
          <button type="button" onClick={() => onOut(i)} className="flex min-w-0 max-w-[160px] items-center gap-[6px] whitespace-nowrap pl-[9px] text-[color:var(--muted)] hover:text-[color:var(--ink)]">
            <span className="min-w-0 truncate">{traceCanvasSnapshot(id).title || "Untitled workspace"}</span>
            <span aria-hidden="true" className="text-[color:var(--muted-soft)]">&rsaquo;</span>
          </button>
        </HoverTooltip>
      ))}
    </div>
  );
}

// Where a pane's next board comes up from: the card it went in by, on screen
// on the board it went in from, for the board coming up to take.
const enterFrom = new Map();

// One workspace's board in a pane: what it holds, read for its board (its
// searches' sessions, its machines', the workspaces inside it), and the
// pane's cells in its bar. A pane keeps the boards it went in through laid
// out under the one it shows, unseen, so going back out finds each as it
// was. `flash` points a card out on it.
function Level({ ws, top, pane, count, canSplit, active, api, range, onRangeChange, handed, workspaces, sheetOpen, model, flash = null }) {
  // A session previewed over the panes is the workspace in front while it is
  // up: the boards under it tell the page nothing about themselves until it
  // goes, when they tell it again.
  const previewUp = !!useTracePreview();
  const c = useTraceCanvas(ws);
  const searches = useSearchRows(c.searches, range, model);
  const nested = useNestedRows(c.nested, range, model, workspaces);
  // The machines it holds, each with its sessions in the range.
  const machines = useMemo(() => (c.machines?.length ? c.machines.map((m) => {
    const { name, person } = machineLabel(m.id);
    return {
      id: m.id, name: m.name || name, person, status: model.settled ? "ready" : "reading",
      sessions: (model.byId.get(m.id)?.sessions || []).map((x) => ({ id: x.id, title: x.title, firstMs: x.firstMs, lastMs: x.lastMs, findings: x.findings })),
    };
  }) : null), [c.machines, model]);
  const lead = useMemo(() => (
    <>
      <Trail trail={pane.trail} onOut={(i) => api.out(pane.id, i)} />
      <WorkspaceMenu workspaces={workspaces} current={ws} onPick={(id) => api.setWs(pane.id, id)} dim={!active && count > 1} edge="r" />
    </>
  ), [workspaces, ws, pane.id, pane.trail, api, active, count]);
  const tail = useMemo(() => <PaneEnds paneId={pane.id} count={count} canSplit={canSplit} api={api} />, [pane.id, count, canSplit, api]);
  const onAdd = useCallback(() => api.find(pane.id), [api, pane.id]);
  // A trace's own workspace is called what its trace is; one of the reader's
  // own keeps the name it started with.
  const onTitle = useCallback((t) => { if (String(ws).startsWith("trace:")) setTraceCanvasTitle(ws, t); }, [ws]);
  const onEnter = useCallback((id, from, focus = null) => {
    if (from) enterFrom.set(pane.id, { ws: id, from, at: performance.now() });
    api.into(pane.id, id, focus);
  }, [api, pane.id]);
  const onExit = useCallback(() => api.out(pane.id, pane.trail.length - 1), [api, pane.id, pane.trail.length]);
  // Closed, the pane starts again (the overview, with no other pane); the
  // workspace stays kept.
  const onClose = useCallback(() => api.setWs(pane.id, null), [api, pane.id]);
  // Clear takes everything off the workspace, the reader's notes too: it is
  // let go of, and the pane starts again (Pane).
  const onClear = useCallback(() => emptyTraceCanvas(ws), [ws]);
  // A cell picked on a board with no trace's reading behind it reads in the
  // home's dock; the board in front lends that dock its own tabs.
  const onCellPick = useCallback((pick) => setBoardPick({ ...pick, board: ws }), [ws]);
  useEffect(() => () => clearBoardPick(ws), [ws]);
  // The trace selected on the board in front reads in the home's dock too:
  // the board stops saying which once it is not in front, and its trace goes.
  const front = active && top;
  const onCardSelect = useCallback((card, key = null) => (card ? setBoardCard({ ...card, board: ws }) : clearBoardCard(ws, key)), [ws]);
  useEffect(() => (front ? () => clearBoardCard(ws) : undefined), [front, ws]);
  // Going in, the board opens out of the card it was, the one it was inside
  // showing under it until it has; coming back out, the one outside settles
  // back from over it.
  const ref = useRef(null);
  const wasTop = useRef(top);
  const [under, setUnder] = useState(false);
  useLayoutEffect(() => {
    const was = wasTop.current;
    wasTop.current = top;
    if (top && !was && ref.current?.animate) {
      ref.current.animate([{ transform: "scale(1.04)", opacity: 0.4 }, { transform: "scale(1)", opacity: 1 }], { duration: 240, easing: "cubic-bezier(0.2, 0.8, 0.2, 1)" });
    }
    if (top || !was) return undefined;
    setUnder(true);
    const t = setTimeout(() => setUnder(false), OPEN_MS + 40);
    return () => clearTimeout(t);
  }, [top]);
  useLayoutEffect(() => {
    if (!top || !ref.current?.animate || !pane.trail.length) return;
    // Run again (as development runs effects twice), it starts over.
    for (const a of ref.current.getAnimations()) a.cancel();
    const got = enterFrom.get(pane.id);
    const r = ref.current.getBoundingClientRect();
    if (got?.ws === ws && performance.now() - got.at < 1000 && r.width && r.height) {
      const { from } = got;
      const inset = [from.top - r.top, r.right - from.left - from.width, r.bottom - from.top - from.height, from.left - r.left].map((v) => `${Math.max(0, Math.round(v))}px`).join(" ");
      ref.current.animate([
        { clipPath: `inset(${inset} round 2px)`, opacity: 0.7 },
        { clipPath: "inset(0px 0px 0px 0px round 0px)", opacity: 1 },
      ], { duration: OPEN_MS, easing: "cubic-bezier(0.2, 0.8, 0.2, 1)" });
    } else {
      ref.current.animate([{ transform: "scale(0.94)", opacity: 0 }, { transform: "scale(1)", opacity: 1 }], { duration: 260, easing: "cubic-bezier(0.2, 0.8, 0.2, 1)" });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div
      ref={ref}
      className={top ? "relative flex min-h-0 min-w-0 flex-1 flex-col" : `pointer-events-none absolute inset-0 flex min-h-0 min-w-0 flex-col ${under ? "" : "invisible"}`}
      aria-hidden={top ? undefined : true}
      data-deck-board={top ? ws : undefined}
      data-deck-level={ws}
    >
      <TraceCanvas
        canvasId={ws}
        trace={handed?.trace || null}
        focus={handed?.focus || null}
        range={range}
        onRangeChange={onRangeChange}
        onAddRequest={onAdd}
        addOpen={sheetOpen && active && top}
        onTitle={onTitle}
        dockBridge
        dockFront={front}
        publish={!previewUp}
        onCellPick={onCellPick}
        onCardSelect={front ? onCardSelect : null}
        barLead={lead}
        barTail={tail}
        framed
        machines={machines}
        searches={searches}
        nested={nested}
        onEnter={onEnter}
        onExit={pane.trail.length ? onExit : null}
        onClose={top ? onClose : null}
        flash={flash}
        onClear={onClear}
        clearLabel="Take everything off this workspace, your notes too, and start another here"
      />
    </div>
  );
}

// A pane: a workspace's board, framed and footed by the board itself, with
// the pane's own cells in the board's bar (where it went in from, which
// workspace, Split, close); or, with none, where one starts, in a frame of
// the pane's own.
function Pane({ pane, count, canSplit, active, cover, api, range, onRangeChange, handed, workspaces, sheetOpen, model }) {
  // A workspace emptied in a pane (cleared, or its last thing taken off) is
  // let go of, and the pane is blank again: where workspaces start.
  const holds = traceCanvasHolds(pane.ws);
  const emptied = !!pane.ws && traceCanvasKept(pane.ws) && !holds;
  // One the reader deleted: the pane is blank again, with no word of it lost.
  const deleted = traceCanvasDeleted(pane.ws);
  useEffect(() => {
    if (!emptied && !deleted) return;
    if (emptied) forgetEmptyTraceCanvas(pane.ws);
    api.setWs(pane.id, null);
  }, [emptied, deleted, pane.ws, pane.id, api]);
  const kept = !!pane.ws && holds;
  const [over, setOver] = useState(null);
  // The foot band's middle, for the blank page's own cells.
  const [foot, setFoot] = useState(null);
  const levels = kept ? [...pane.trail.filter((id) => traceCanvasKept(id)), pane.ws] : [];
  // Back out of a workspace, the one it was inside points out its card.
  const [cameFrom, setCameFrom] = useState(null);
  const was = useRef({ ws: pane.ws, trail: pane.trail });
  useLayoutEffect(() => {
    const prev = was.current;
    was.current = { ws: pane.ws, trail: pane.trail };
    if (prev.ws === pane.ws || !prev.trail.includes(pane.ws)) return;
    const chain = [...prev.trail, prev.ws];
    const child = chain[chain.indexOf(pane.ws) + 1];
    if (child) setCameFrom((c) => ({ key: `graph:workspace:${child}`, n: (c?.n || 0) + 1 }));
  }, [pane.ws, pane.trail]);
  const onAdd = useCallback(() => api.find(pane.id), [api, pane.id]);
  return (
    <section
      className={`flex h-full min-h-0 min-w-0 flex-col ${kept ? "" : "overflow-hidden rounded-[2px] border border-[color:var(--line)] bg-[color:var(--bg)]"}`}
      aria-label={kept ? traceCanvasSnapshot(pane.ws).title || "Workspace" : "No workspace"}
      data-deck-pane={pane.id}
      data-deck-active={active ? "" : undefined}
      onPointerDownCapture={() => api.focus(pane.id)}
      onDragOver={(e) => {
        if (!draggingItem(e)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
        const what = [...(e.dataTransfer?.types || [])].includes(WORKSPACE_DRAG_TYPE) ? "workspace" : "thing";
        if (over !== what) setOver(what);
        // Its board shows where the thing would land, as it is carried.
        if (kept) noteBoardDragOver(pane.ws, draggedItem(), e.clientX, e.clientY);
      }}
      onDragLeave={(e) => {
        if (e.currentTarget.contains(e.relatedTarget)) return;
        setOver(null);
        if (kept) noteBoardDragOver(pane.ws, null);
      }}
      onDrop={(e) => {
        if (!draggingItem(e)) return;
        e.preventDefault();
        setOver(null);
        if (kept) noteBoardDragOver(pane.ws, null);
        e.nativeEvent.laidOn = true;
        const item = droppedItem(e);
        if (!item) return;
        // On a workspace's board, what it brings lands where it was let go.
        if (kept) noteBoardDrop(pane.ws, item, e.clientX, e.clientY);
        api.layInto(pane.id, item);
      }}
    >
      <PaneFootContext.Provider value={kept ? null : foot}>
      <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
        {kept ? levels.map((id, i) => (
          <Level
            key={id}
            ws={id}
            top={i === levels.length - 1}
            pane={pane}
            count={count}
            canSplit={canSplit}
            active={active}
            api={api}
            range={range}
            onRangeChange={onRangeChange}
            handed={i === levels.length - 1 ? handed : null}
            workspaces={workspaces}
            sheetOpen={sheetOpen}
            model={model}
            flash={i === levels.length - 1 ? cameFrom : null}
          />
        )) : (
          <Start lost={!!pane.ws && !emptied && !deleted} cover={cover} onStart={(things) => api.startWith(pane.id, things)} range={range} active={active} />
        )}
        {over ? (
          <DropCue
            slim={kept}
            text={!kept ? (over === "workspace" ? "Drop to start a workspace with it inside" : "Drop to start a workspace on it")
              : over === "workspace" ? "Drop to put it inside this workspace" : "Drop to lay it on this workspace"}
          />
        ) : null}
      </div>
      </PaneFootContext.Provider>
      {kept ? null : (
        <div className="flex h-[32px] min-w-0 flex-none items-stretch border-t border-[color:var(--line-soft)] bg-[color:var(--bg)] text-[11px]" data-deck-controls="">
          <WorkspaceMenu workspaces={workspaces} current={null} onPick={(id) => api.setWs(pane.id, id)} dim={!active && count > 1} edge="r" />
          <div ref={setFoot} className="flex min-w-0 flex-1 items-stretch justify-center" data-deck-foot="" />
          <div className="ml-auto flex flex-none items-stretch">
            <HoverTooltip placement="top" label="What met a standard, the triage queue, the machines and every session, to bring onto a workspace (/)">
              <button type="button" className={`${cell} ${sheetOpen && active ? cellOn : ""}`} onClick={onAdd} aria-pressed={sheetOpen && active} data-deck-find="">+ Add</button>
            </HoverTooltip>
            <PaneEnds paneId={pane.id} count={count} canSplit={canSplit} api={api} />
          </div>
        </div>
      )}
    </section>
  );
}

// -- the deck -----------------------------------------------------------------------

// A blank pane in front (nothing started in it yet) takes up the chat in
// front: as the chat finds its first traces, its investigation's workspace
// (the one its graph opens) opens in the pane, and follows the chat from
// then on. Only for an investigation with no workspace yet, and only on the
// traces the chat finds while the pane is blank (a graph loaded again counts
// none): an old one, or one put away, stays where it is.
function useBlankPaneFollowsChat(blank) {
  const chatBar = useSyncExternalStore(subscribeDock, getDockChatBar, () => null);
  const [key, setKey] = useState(null);
  // Which chat is in front has no event of its own (a board reads it the
  // same way): read again as the dock's chats change, and on a slow beat.
  useEffect(() => {
    if (!blank) { setKey(null); return undefined; }
    const read = () => setKey(parseGraphFocus(activeGraphKey()));
    read();
    const t = setInterval(read, 1500);
    return () => clearInterval(t);
  }, [blank, chatBar]);
  const subscribe = useCallback((cb) => (key ? subscribeGraph(key, cb) : () => {}), [key]);
  const version = useSyncExternalStore(subscribe, () => (key ? graphVersion(key) : 0), () => 0);
  // How many traces the chat's claims rested on when the pane went blank
  // under it: a claim on a new one opens the chat's workspace, never a trace
  // a search only named.
  const since = useRef(null);
  useEffect(() => {
    if (!blank || !key) { since.current = null; return; }
    const graph = readLiveGraph(key);
    const traces = workspaceTraces(graph).length;
    if (since.current?.key !== key) { since.current = { key, traces }; return; }
    if (traces <= since.current.traces || traceCanvasKept(chatCanvasId(key))) return;
    const title = (getDockChatBar()?.tabs || []).find((t) => t.active)?.label || "";
    openGraphCanvas({ graph, graphKey: key, title });
  }, [blank, key, version]);
}

export default function WorkspaceDeck({ range = "1M", onRangeChange = null }) {
  const [deck, setDeck] = useState(readDeck);
  const deckRef = useRef(deck);
  deckRef.current = deck;
  useBlankPaneFollowsChat(!deck.panes.find((p) => p.id === deck.active)?.ws);
  const put = useCallback((next, keep = true) => {
    deckRef.current = next;
    setDeck(next);
    if (keep) writeDeck(next);
  }, []);
  const rootRef = useRef(null);
  const [rootW, setRootW] = useState(0);
  const rootWRef = useRef(0);
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;
    const measure = () => {
      rootWRef.current = el.getBoundingClientRect().width;
      setRootW(rootWRef.current);
    };
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // What an opener handed a workspace's board (a trace page's record, a claim
  // to bring into view), for the pane it opens in.
  const handed = useRef(new Map());
  const workspaces = useTraceCanvases();
  // The fleet reads what its sessions were about only for what shows them by
  // title: the sheet, and a workspace holding machines.
  const heldMachines = deck.panes.some((p) => [p.ws, ...p.trail].some((id) => id && workspaces.find((w) => w.id === id)?.machines));
  const model = useFleetModel(range, { heads: deck.sheet || heldMachines });
  // The sidebar's sessions go into the search when the pane worked in shows
  // the range's board rather than a workspace: one with none, or with one no
  // longer kept here.
  const activeWs = deck.panes.find((p) => p.id === deck.active)?.ws || null;
  const blankInFront = !activeWs || !workspaces.some((w) => w.id === activeWs);
  useEffect(() => { setDeckBlankInFront(blankInFront); }, [blankInFront]);
  useEffect(() => () => setDeckBlankInFront(false), []);
  const outcomes = useMemo(() => outcomesOf(model), [model]);

  // The workspace worked in is the store's open canvas, so the chat's
  // workspace tools act on it; a pane let go of lets it go.
  useEffect(() => {
    const p = deck.panes.find((x) => x.id === deck.active);
    if (p?.ws && traceCanvasKept(p.ws)) showTraceCanvasInDeck(p.ws);
  }, [deck.active, deck.panes]);
  const shownRef = useRef(new Set());
  useEffect(() => {
    const now = new Set(deck.panes.map((p) => p.ws).filter(Boolean));
    for (const ws of shownRef.current) if (!now.has(ws)) releaseTraceCanvasInDeck(ws);
    shownRef.current = now;
  }, [deck.panes]);
  // Left for another mode, the deck lets its workspaces go: none is worked in
  // there, and none is laid over that mode's page.
  useEffect(() => () => {
    for (const ws of shownRef.current) releaseTraceCanvasInDeck(ws);
  }, []);

  const flash = (paneId) => {
    const el = rootRef.current?.querySelector(`[data-deck-pane="${paneId}"]`);
    if (!el) return;
    el.style.boxShadow = "inset 0 0 0 1px var(--green)";
    setTimeout(() => el.style.removeProperty("box-shadow"), 700);
  };

  const api = useMemo(() => {
    const evenOut = (panes) => {
      const sum = panes.reduce((n, p) => n + p.w, 0);
      const room = rootWRef.current - GUTTER * (panes.length - 1);
      return panes.some((p) => (p.w / sum) * room < PANE_MIN) ? panes.map((p) => ({ ...p, w: 1 })) : panes;
    };
    // A workspace opened: shown where it already is, else in the pane being
    // worked in (`fromPane`), in place of what that showed. A pane is only
    // ever added by Split. Shown where it already is, it is the pane's again
    // (opened over another page first, it was not), so closing the pane lets
    // it go.
    const place = (ws, fromPane = null) => {
      const s = deckRef.current;
      const shown = s.panes.find((p) => p.ws === ws);
      if (shown) {
        put({ ...s, active: shown.id });
        flash(shown.id);
        if (traceCanvasKept(ws)) showTraceCanvasInDeck(ws);
        return shown.id;
      }
      const into = s.panes.find((p) => p.id === fromPane) || s.panes.find((p) => p.id === s.active) || s.panes[0];
      put({ ...s, active: into.id, panes: s.panes.map((p) => (p.id === into.id ? { ...p, ws, trail: [] } : p)) });
      return into.id;
    };
    return {
      place,
      focus: (id) => {
        const s = deckRef.current;
        if (s.active !== id) put({ ...s, active: id });
      },
      // What was dropped on a pane, or picked while it is the one worked in,
      // onto its workspace: a session laid on, a finding kept (its session
      // laid on), a machine kept as itself, a workspace put inside. On a pane
      // without one, a workspace of its own opens on it, holding just that
      // (named for it, but for a workspace inside).
      layInto: (paneId, item) => {
        const s = deckRef.current;
        const pane = s.panes.find((p) => p.id === paneId);
        if (!pane || !item) return;
        // A whole workspace goes inside the one it is dropped on (never into
        // itself); on a pane without one, inside a workspace of its own, so a
        // workspace can be built of workspaces alone.
        if (item.kind === "workspace") {
          if (!traceCanvasKept(item.id)) return;
          if (pane.ws && traceCanvasKept(pane.ws)) {
            if (!reaches(item.id, pane.ws)) keepOnTraceCanvas(pane.ws, { nested: [{ id: item.id }] });
            if (s.active !== paneId) put({ ...s, active: paneId });
            return;
          }
          const ws = newTraceCanvas({ nested: [{ id: item.id }] });
          if (!ws) return;
          put({ ...s, active: paneId, panes: s.panes.map((p) => (p.id === paneId ? { ...p, ws } : p)) });
          return;
        }
        const all = (item.sessions || []).filter((x) => x?.id).map((x) => ({ id: String(x.id).toLowerCase(), title: x.title || "" }));
        const machines = item.kind === "machine" ? [{ id: item.id, name: item.name || "" }] : [];
        const findings = item.kind === "finding" ? [{ id: item.id, session: item.session, title: item.title || "" }] : [];
        const sessions = item.kind === "machine" ? [] : all;
        if (pane.ws && traceCanvasKept(pane.ws)) {
          if (sessions.length) layOnTraceCanvas(pane.ws, sessions);
          if (machines.length || findings.length) keepOnTraceCanvas(pane.ws, { machines, findings });
          if (sessions.length) requestWorkspaceFocus(sessions[0].id);
          if (s.active !== paneId) put({ ...s, active: paneId });
          return;
        }
        const ws = newTraceCanvas({ title: item.name || item.title || sessions[0]?.title || "", sessions, machines, findings });
        if (!ws) return;
        put({ ...s, active: paneId, panes: s.panes.map((p) => (p.id === paneId ? { ...p, ws } : p)) });
      },
      // Notes, searches and sessions onto a pane's workspace, or a workspace
      // of its own started on them.
      startWith: (paneId, things) => {
        const s = deckRef.current;
        const pane = s.panes.find((p) => p.id === paneId);
        if (!pane) return;
        if (pane.ws && traceCanvasKept(pane.ws)) {
          keepOnTraceCanvas(pane.ws, things);
          if (things.sessions?.length) layOnTraceCanvas(pane.ws, things.sessions);
          if (s.active !== paneId) put({ ...s, active: paneId });
          return;
        }
        const ws = newTraceCanvas({ title: things.searches?.[0]?.label || "", ...things });
        if (ws) put({ ...s, active: paneId, panes: s.panes.map((p) => (p.id === paneId ? { ...p, ws } : p)) });
      },
      setWs: (paneId, ws) => {
        const s = deckRef.current;
        if (ws && s.panes.some((p) => p.ws === ws && p.id !== paneId)) {
          const other = s.panes.find((p) => p.ws === ws);
          put({ ...s, active: other.id });
          flash(other.id);
          return;
        }
        put({ ...s, active: paneId, panes: s.panes.map((p) => (p.id === paneId ? { ...p, ws: ws || null, trail: [] } : p)) });
      },
      // Into a workspace inside the one a pane shows, the way back kept.
      // `focus`: one of its sessions to come in on (its card brought into
      // view), or none.
      into: (paneId, child, focus = null) => {
        const s = deckRef.current;
        const pane = s.panes.find((p) => p.id === paneId);
        if (!pane?.ws || !traceCanvasKept(child) || child === pane.ws) return;
        const was = handed.current.get(child);
        if (focus || was?.focus) handed.current.set(child, { ...was, focus: focus || null });
        const back = pane.trail.indexOf(child);
        const trail = back >= 0 ? pane.trail.slice(0, back) : [...pane.trail, pane.ws].slice(-MAX_TRAIL);
        put({ ...s, active: paneId, panes: s.panes.map((p) => (p.id === paneId ? { ...p, ws: child, trail } : p)) });
      },
      // Back out to the workspace `depth` steps from the outermost.
      out: (paneId, depth) => {
        const s = deckRef.current;
        const pane = s.panes.find((p) => p.id === paneId);
        if (!pane || depth < 0 || depth >= pane.trail.length) return;
        put({ ...s, active: paneId, panes: s.panes.map((p) => (p.id === paneId ? { ...p, ws: pane.trail[depth], trail: pane.trail.slice(0, depth) } : p)) });
      },
      split: (paneId) => {
        const s = deckRef.current;
        const i = s.panes.findIndex((p) => p.id === paneId);
        if (i < 0 || s.panes.length >= MAX_PANES) return;
        const cur = s.panes[i];
        const next = freshPane(null, cur.w / 2);
        put({ ...s, active: next.id, panes: evenOut([...s.panes.slice(0, i), { ...cur, w: cur.w / 2 }, next, ...s.panes.slice(i + 1)]) });
      },
      close: (paneId) => {
        const s = deckRef.current;
        const i = s.panes.findIndex((p) => p.id === paneId);
        if (i < 0 || s.panes.length < 2) return;
        const gone = s.panes[i];
        const panes = s.panes.filter((p) => p.id !== paneId);
        const j = Math.max(0, i - 1);
        panes[j] = { ...panes[j], w: panes[j].w + gone.w };
        put({ ...s, panes, active: s.active === paneId ? panes[j].id : s.active });
      },
      // The sheet, up over the plane for the pane `paneId` (the one a pick
      // lays onto), or put away when it is already up for it.
      find: (paneId = null) => {
        const s = deckRef.current;
        const id = paneId || s.active;
        if (s.sheet && s.active === id) put({ ...s, sheet: false });
        else put({ ...s, sheet: true, active: id });
      },
      closeSheet: () => {
        const s = deckRef.current;
        if (s.sheet) put({ ...s, sheet: false });
      },
      setTab: (tab) => {
        const s = deckRef.current;
        if (s.tab !== tab) put({ ...s, tab });
      },
      put,
    };
  }, [put]);

  useEffect(() => onDeckWorkspace((id, extra) => {
    if (!id) {
      if (extra?.fresh) api.setWs(deckRef.current.active, null);
      return;
    }
    if (extra?.trace || extra?.focus) handed.current.set(id, extra);
    api.place(id, deckRef.current.active);
  }), [api]);

  // "/" brings the sheet up, or puts it away, wherever the keys are not
  // being typed into, and no dialog over the page (a session previewed) has
  // them.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.target?.closest?.("input, textarea, select, [contenteditable='true'], .cm-editor")) return;
      if (!rootRef.current?.isConnected || document.querySelector('[aria-modal="true"]')) return;
      e.preventDefault();
      api.find();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [api]);

  // Sharing the room between two panes.
  const sizing = useRef(null);
  const startResize = (i, clientX) => {
    const slots = [...(rootRef.current?.querySelectorAll("[data-deck-pane-slot]") || [])];
    const a = slots[i - 1]?.getBoundingClientRect();
    const b = slots[i]?.getBoundingClientRect();
    if (!a || !b) return;
    const panes = deckRef.current.panes;
    sizing.current = { i, x: clientX, aw: a.width, bw: b.width, wsum: panes[i - 1].w + panes[i].w };
  };
  const moveResize = (clientX) => {
    const r = sizing.current;
    if (!r) return;
    const total = r.aw + r.bw;
    const aw = Math.max(PANE_MIN, Math.min(total - PANE_MIN, r.aw + clientX - r.x));
    const s = deckRef.current;
    put({ ...s, panes: s.panes.map((p, j) => (j === r.i - 1 ? { ...p, w: (r.wsum * aw) / total } : j === r.i ? { ...p, w: (r.wsum * (total - aw)) / total } : p)) }, false);
  };
  const endResize = () => {
    sizing.current = null;
    writeDeck(deckRef.current);
  };
  // Unmeasured (a test's DOM), there is room.
  const canSplit = deck.panes.length < MAX_PANES && (!rootW || rootW / (deck.panes.length + 1) >= PANE_MIN);

  // The sheet: what a pick does depends on the pane being worked in. On its
  // workspace, a pick is laid on; on the range's board, it goes into the
  // board's search, as from the board's own lists (`collect`).
  const target = deck.panes.find((p) => p.id === deck.active) || deck.panes[0];
  const targetC = useTraceCanvas(target?.ws);
  const targetKept = !!target?.ws && traceCanvasKept(target.ws);
  const [picks] = useWorkspacePicks();
  const traces = useMemo(() => (targetKept ? [...(targetC.anchor ? [{ id: targetC.anchor, title: targetC.title }] : []), ...targetC.layered] : picks), [targetKept, targetC.anchor, targetC.title, targetC.layered, picks]);
  const layItem = useCallback((item) => {
    const s = deckRef.current;
    const pane = s.panes.find((p) => p.id === s.active);
    if (pane?.ws && traceCanvasKept(pane.ws)) api.layInto(s.active, item);
    else collectInSearch(item);
  }, [api]);
  const openOwn = useCallback((s) => {
    const ws = keepTraceCanvas({ id: s.id, title: s.title || "" });
    if (ws) api.place(ws, deckRef.current.active);
  }, [api]);
  const extraSources = useCallback(({ needle, on }) => fleetSources({ model, outcomes, needle, on, onItem: layItem, onOpen: openOwn, collect: !targetKept }), [model, outcomes, layItem, openOwn, targetKept]);
  // The sheet stands over the sessions' list and a little of the plane: the
  // page's sources side, so the workspaces stay in view to drop onto. Its
  // edges are the page's own lines: the list's edge on the left, the panes'
  // tops above, the top of the bars along their feet below.
  const [sheetAt, setSheetAt] = useState(null);
  useLayoutEffect(() => {
    if (!deck.sheet) { setSheetAt(null); return undefined; }
    const measure = () => {
      const r = rootRef.current?.getBoundingClientRect();
      if (!r) return;
      const list = document.querySelector("[data-workspace-threads]")?.getBoundingClientRect();
      const left = list && list.width > 40 ? list.left : r.left + GUTTER;
      const width = Math.round(Math.max(460, Math.min(560, r.left - left + 220)));
      setSheetAt({ left, top: r.top, width, bottom: window.innerHeight - (r.bottom - BAR_H - 1), cover: Math.max(0, left + width - r.left) });
    };
    measure();
    window.addEventListener("resize", measure);
    const ro = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    ro?.observe(rootRef.current);
    return () => { window.removeEventListener("resize", measure); ro?.disconnect(); };
  }, [deck.sheet, rootW]);
  const sheetW = sheetAt?.width || 0;
  // Something dragged off the sheet is on its way to a pane: the page around
  // it stops standing in the way until it lands.
  const [sheetDrag, setSheetDrag] = useState(false);
  useEffect(() => {
    if (!deck.sheet) return undefined;
    const on = () => setSheetDrag(true);
    const off = () => setSheetDrag(false);
    document.addEventListener("dragstart", on);
    document.addEventListener("dragend", off);
    document.addEventListener("drop", off);
    return () => {
      document.removeEventListener("dragstart", on);
      document.removeEventListener("dragend", off);
      document.removeEventListener("drop", off);
      setSheetDrag(false);
    };
  }, [deck.sheet]);
  const downOnScrim = useRef(false);
  const subtitle = targetKept
    ? `Drag anything onto a workspace, or click it to lay it on "${targetC.title || "the workspace"}"`
    : "Click a session to add it to the board's search, or drag anything onto a pane to start a workspace there";

  return (
    <div ref={rootRef} className="relative flex h-full min-h-0 min-w-0" data-workspace-deck="">
      {deck.panes.map((p, i) => (
        <Fragment key={p.id}>
          {i ? <PaneDivider onStart={(x) => startResize(i, x)} onMove={moveResize} onEnd={endResize} /> : null}
          <div className="flex min-h-0 min-w-0 flex-col" style={{ flex: `${p.w} 1 0px` }} data-deck-pane-slot={p.id}>
            <Pane
              pane={p}
              count={deck.panes.length}
              canSplit={canSplit}
              active={p.id === deck.active}
              cover={deck.sheet && i === 0 ? (sheetAt?.cover || 0) + GUTTER : 0}
              api={api}
              range={range}
              onRangeChange={onRangeChange}
              handed={p.ws ? handed.current.get(p.ws) : null}
              workspaces={workspaces}
              sheetOpen={deck.sheet}
              model={model}
            />
          </div>
        </Fragment>
      ))}
      {deck.sheet && sheetW ? createPortal(
        <>
          {/* The page around the sheet, veiled in its own ground (light on a
              light theme, dark on a dark one): a click there puts the sheet
              away. */}
          <div
            aria-hidden="true"
            onPointerDown={(e) => { downOnScrim.current = e.target === e.currentTarget; }}
            onClick={(e) => {
              if (downOnScrim.current && e.target === e.currentTarget) api.closeSheet();
              downOnScrim.current = false;
            }}
            className={`fixed inset-0 z-[39] cursor-pointer bg-[color:color-mix(in_srgb,var(--bg)_60%,transparent)] backdrop-saturate-[0.6] transition-colors hover:bg-[color:color-mix(in_srgb,var(--bg)_70%,transparent)] ${sheetDrag ? "pointer-events-none opacity-0" : ""}`}
            data-workspace-sheet-scrim=""
          />
          <div
            className="fixed z-[40] flex min-h-0 flex-col rounded-[2px] shadow-[var(--node-shadow-lift)]"
            style={{ top: sheetAt.top, left: sheetAt.left, bottom: sheetAt.bottom, width: sheetW }}
            data-workspace-sheet=""
          >
            <WorkspaceAddPanel
              range={range}
              onRangeChange={onRangeChange}
              onClose={api.closeSheet}
              floating
              traces={traces}
              anchor={targetKept ? targetC.anchor || null : null}
              onLay={(entry) => layItem({ kind: "session", sessions: [entry] })}
              chat={false}
              canvases={workspaces}
              activeCanvas={targetKept ? target.ws : null}
              onOpenCanvas={(id) => api.place(id, deckRef.current.active)}
              onNestCanvas={targetKept ? (id) => api.layInto(deckRef.current.active, { kind: "workspace", id, sessions: [] }) : null}
              extraSources={extraSources}
              initialSource={deck.tab}
              onSourceChange={api.setTab}
              hideSources={HIDDEN_SOURCES}
              title={targetKept ? "Bring onto a workspace" : "Add to the search"}
              subtitle={subtitle}
              collect={!targetKept}
              onKeepSearch={(q) => (targetKept ? api.startWith(deckRef.current.active, { searches: [{ q, label: q }] }) : setWorkspaceSearch((t) => appendClause(t, q), { now: true }))}
              onNote={() => api.startWith(deckRef.current.active, { looseNotes: [{ text: "" }] })}
            />
          </div>
        </>,
        document.body,
      ) : null}
    </div>
  );
}

// The panel's Flagged list is Triage here, by where each finding was left.
const HIDDEN_SOURCES = ["flagged"];

// What is picked off the sheet while the pane worked in shows the range's
// board goes into the board's search: one session (or a finding's) in, or
// out when it is in; several in at once; a machine narrows the search to it,
// as drilling down to its card does.
function collectInSearch(item) {
  if (!item) return;
  if (item.kind === "machine") {
    const clause = STEPS.machine.clause(item.id);
    if (clause) setWorkspaceSearch((t) => appendClause(t, clause), { now: true });
    return;
  }
  const sessions = (item.sessions || []).filter((x) => x?.id).map((x) => ({ id: String(x.id).toLowerCase(), title: x.title || "" }));
  if (sessions.length === 1) togglePick(sessions[0]);
  else if (sessions.length) addPicks(sessions);
}
